import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import logger from './utils/logger';

// Import translation files
import translationEN from './locales/en/translation.json';
import translationHE from './locales/he/translation.json';
import translationNL from './locales/nl/translation.json';

// The translations
const resources = {
  en: {
    translation: translationEN
  },
  he: {
    translation: translationHE
  },
  nl: {
    translation: translationNL
  }
};

// Language code mapping from user settings to i18n codes
export const languageCodeMap: Record<string, string> = {
  english: 'en',
  hebrew: 'he',
  dutch: 'nl'
};

// Reverse mapping from i18n codes to user setting values
export const reverseLanguageCodeMap: Record<string, string> = {
  en: 'english',
  he: 'hebrew',
  nl: 'dutch'
};

// RTL languages
export const rtlLanguages = ['he'];

// Update document direction based on language
export const setDocumentDirection = (languageCode: string, isInitialLoad: boolean = false): void => {
  const isRTL = rtlLanguages.includes(languageCode);
  document.documentElement.dir = isRTL ? 'rtl' : 'ltr';

  // Only log on initial load or in development mode
  if (isInitialLoad || process.env.NODE_ENV === 'development') {
    logger.info(`[Language] Setting document direction: ${isRTL ? 'RTL' : 'LTR'} for language ${languageCode}`);
  }

  // Store the direction preference
  localStorage.setItem('documentDirection', isRTL ? 'rtl' : 'ltr');

  if (isInitialLoad) {
    // Store the language preference
    localStorage.setItem('i18nextLng', languageCode);
  }
};

// Get initial language from storage or default
const getInitialLanguage = (): string => {
  const storedLang = localStorage.getItem('i18nextLng');
  if (storedLang && Object.values(languageCodeMap).includes(storedLang)) {
    logger.info('[Language] Using stored language:', storedLang);
    return storedLang;
  }
  logger.info('[Language] No valid stored language, defaulting to:', 'en');
  return 'en';
};

// Set initial document direction - do this before i18n initialization
const initialLanguage = getInitialLanguage();
setDocumentDirection(initialLanguage);

// Initialize i18n with stored or default language
i18n
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    lng: initialLanguage,
    debug: process.env.NODE_ENV === 'development',

    interpolation: {
      escapeValue: false // React already safes from XSS
    },

    // RTL languages configuration
    supportedLngs: ['en', 'he', 'nl'],
  });

// Function to set language based on user's native language setting
export const setLanguageFromUserSettings = (nativeLanguage: string, isInitialLoad: boolean = false): void => {
  // Check if we've already set this language in this session
  const lastSetLanguage = sessionStorage.getItem('last_set_language');
  const languageCode = languageCodeMap[nativeLanguage.toLowerCase()] || 'en';

  // If this is the same language we've already set in this session and not initial load, do nothing
  if (!isInitialLoad && lastSetLanguage === languageCode) {
    return;
  }

  // Set for future reference
  sessionStorage.setItem('last_set_language', languageCode);

  // Convert user setting to i18n language code
  if (isInitialLoad) {
    logger.info('[Language] Setting language from user settings:', { nativeLanguage, languageCode });
  }

  // Only change if it's different
  if (i18n.language !== languageCode) {
    if (isInitialLoad) {
      logger.info('[Language] Changing language:', { from: i18n.language, to: languageCode });
    }
    i18n.changeLanguage(languageCode);
    setDocumentDirection(languageCode, isInitialLoad);
    localStorage.setItem('i18nextLng', languageCode); // Persist language preference
  } else if (isInitialLoad) {
    // Even if language is the same, ensure direction is correct, but only on initial load
    setDocumentDirection(languageCode, isInitialLoad);
  }
};

// Add language change listener
i18n.on('languageChanged', (lng) => {
  logger.info('[Language] Language changed event:', lng);
  setDocumentDirection(lng);
});

export default i18n;